import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LocalLink } from '../../i18n/useLocalizedNav';
import { useAuth } from '../../context/AuthContext';
import Stamp from '../../components/stamps/Stamp';
import CountUp from '../../components/CountUp';
import { LOCATION_COUNT } from '../../content/catalog';

// Stat values are structural; labels are localized via i18n keys. The
// venue count is derived from the catalog (LOCATION_COUNT) so it always
// matches the real number of locations — never hardcode it.
const STATS = [
  { value: LOCATION_COUNT, labelKey: 'home_stat_venues' },
  { value: 10, labelKey: 'home_stat_regions' },
  { value: 5, labelKey: 'home_stat_tiers' },
];

// Photo cards for the hero collage. Each base resolves to a WebP +
// JPG pair at 600px wide, which is enough to cover @2x retina at the
// largest render slot (~256px wide) on either mobile or desktop.
const HERO_PHOTOS = [
  { base: '/images/hero_3', label: 'Hero photo', tone: 'light' },
  { base: '/images/hero_2', label: 'Hero photo', tone: 'dark' },
  { base: '/images/hero_1', label: 'Reel', tone: 'light' },
] as const;

const Hero: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  // Authenticated users already have a passport — send them to it.
  const ctaTo = user ? '/my-stamps' : '/register';
  const ctaLabel = user ? t('home_cta_view_passport') : t('home_cta_claim');

  return (
  <section className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-8 lg:py-12">
    {/* Left — copy */}
    <div className="space-y-7">
      <p className="font-heading font-bold text-xs sm:text-sm tracking-[0.25em] text-brand-accent uppercase">
        {t('home_hero_eyebrow')}
      </p>

      <h1 className="font-display text-warm-800 text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
        {t('home_hero_title_pre')}{' '}
        <span className="italic text-brand-accent">
          {t('home_hero_title_accent')}
        </span>
        ,
        <br />
        {t('home_hero_title_post')}
      </h1>

      <p className="text-lg md:text-xl text-warm-500 max-w-xl leading-relaxed">
        {t('home_hero_lead_1')}{' '}
        <span className="font-bold text-warm-800">
          {t('home_hero_lead_passport')}
        </span>{' '}
        {t('home_hero_lead_2')}{' '}
        <span className="font-bold text-brand-accent">
          {t('home_hero_lead_discounts')}
        </span>
        {t('home_hero_lead_3')}
      </p>

      <div className="flex flex-col gap-3 pt-2">
        {/* Primary CTA — full-width on mobile, sized to content on desktop. */}
        <LocalLink
          to={ctaTo}
          className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all sm:self-start"
        >
          {ctaLabel}
          <ArrowRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" />
        </LocalLink>

        {/* Secondary actions — outlined button + text link on their own row. */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <LocalLink
            to="/locations"
            className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 font-heading font-bold text-warm-800 border-2 border-warm-300 rounded-xl hover:border-brand-primary hover:text-brand-accent transition-all"
          >
            {t('home_view_locations')}
            <ArrowRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" />
          </LocalLink>
          <button
            type="button"
            onClick={() =>
              document
                .getElementById('how-it-works')
                ?.scrollIntoView({ behavior: 'smooth' })
            }
            className="group inline-flex items-center justify-center gap-2 px-4 py-3.5 font-heading font-bold text-warm-600 hover:text-brand-accent transition-colors"
          >
            {t('home_how_it_works')}
            <ArrowRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>

      {/* Mobile-only collage — sits between the CTAs and the stats so
          the user sees the imagery without scrolling past the offer. */}
      <div className="lg:hidden pt-4">
        <Collage scale="mobile" />
      </div>

      <div className="flex items-stretch gap-6 pt-6">
        {STATS.map((stat, i) => (
          <React.Fragment key={stat.labelKey}>
            {i > 0 && <div className="w-px bg-warm-200" />}
            <div className="space-y-1">
              <p className="font-display text-3xl md:text-4xl text-warm-800">
                <CountUp value={stat.value} />
              </p>
              <p className="font-heading font-bold text-[10px] sm:text-xs tracking-[0.15em] text-warm-500 uppercase">
                {t(stat.labelKey)}
              </p>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>

    {/* Desktop right column — same collage, full size. */}
    <div className="hidden lg:block">
      <Collage scale="desktop" />
    </div>
  </section>
  );
};

// Tilted photo + stamp collage. Two scale modes share the same JSX so
// the visual stays consistent: `desktop` runs at full size in the
// right column; `mobile` shrinks the same layout to fit a phone width.
const Collage: React.FC<{ scale: 'mobile' | 'desktop' }> = ({ scale }) => {
  const isMobile = scale === 'mobile';
  return (
    <div
      className={
        isMobile
          ? 'relative mx-auto h-[22rem] w-full max-w-[22rem]'
          : 'relative mx-auto h-[34rem] w-full max-w-lg'
      }
    >
      {/* Photo card — back left */}
      <PhotoCard
        {...HERO_PHOTOS[0]}
        className={
          isMobile
            ? 'absolute left-0 top-4 w-[9.5rem] h-[13.5rem] -rotate-6'
            : 'absolute left-0 top-6 w-[15rem] h-[21rem] -rotate-6'
        }
      />
      {/* Photo card — front center, dark */}
      <PhotoCard
        {...HERO_PHOTOS[1]}
        className={
          isMobile
            ? 'absolute right-2 top-0 w-[10rem] h-[14rem] rotate-3 z-20'
            : 'absolute right-2 top-0 w-[15.5rem] h-[22rem] rotate-3 z-20'
        }
      />
      {/* Photo card — bottom */}
      <PhotoCard
        {...HERO_PHOTOS[2]}
        className={
          isMobile
            ? 'absolute left-6 bottom-0 w-[10.5rem] h-[8rem] rotate-2'
            : 'absolute left-8 bottom-0 w-[16rem] h-[13rem] rotate-2'
        }
      />

      {/* Stamp — earned, top left overlap */}
      <div
        className={
          isMobile
            ? 'absolute left-1 top-28 z-30 drop-shadow-xl'
            : 'absolute left-2 top-44 z-30 drop-shadow-xl'
        }
      >
        <Stamp
          name="Sky Walker"
          category="activity"
          earned
          size={isMobile ? 92 : 148}
        />
      </div>
      {/* Stamp — earned with multiplier, bottom right overlap */}
      <div
        className={
          isMobile
            ? 'absolute right-0 bottom-5 z-30 drop-shadow-xl'
            : 'absolute right-0 bottom-8 z-30 drop-shadow-xl'
        }
      >
        <Stamp
          name="Heritage Baker"
          category="food"
          earned
          multiplier={2}
          size={isMobile ? 98 : 156}
        />
      </div>
    </div>
  );
};

// A single tilted photo card. Renders the image when `base` is set,
// otherwise a striped placeholder with the planned shot label.
// `<picture>` offers WebP first (~45 KiB) and falls back to JPG
// (~95 KiB) for browsers that still don't support WebP.
const PhotoCard: React.FC<{
  base: string | null;
  label: string;
  tone: 'light' | 'dark';
  className?: string;
}> = ({ base, label, tone, className = '' }) => {
  const dark = tone === 'dark';
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-warm-200 shadow-xl ${
        dark ? 'bg-brand-secondary' : 'bg-warm-200'
      } ${className}`}
    >
      {base ? (
        <picture>
          <source type="image/webp" srcSet={`${base}-600.webp`} />
          <img
            src={`${base}-600.jpg`}
            alt=""
            width={600}
            height={800}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        </picture>
      ) : (
        <div
          className={`flex h-full w-full items-center justify-center bg-[repeating-linear-gradient(135deg,transparent,transparent_14px,rgba(0,0,0,0.04)_14px,rgba(0,0,0,0.04)_28px)] ${
            dark ? 'bg-brand-secondary' : ''
          }`}
        >
          <span
            className={`px-4 text-center font-heading text-[10px] font-bold uppercase tracking-[0.18em] ${
              dark ? 'text-brand-primary' : 'text-warm-500'
            }`}
          >
            {label}
          </span>
        </div>
      )}
    </div>
  );
};

export default Hero;
